import moment from 'moment';
import T from 'Components/Helpers/translate';
import Order from 'App/resources/Model/Order';
import Activity from 'App/resources/Model/Activity';
import Appointment from 'App/resources/Model/Appointment';
import ProjectPlan from 'App/resources/Model/ProjectPlan';

import { getAttendingStr } from 'App/helpers/getAttendingStr';
import { currencyFormat } from 'Components/Filters/Currencies';
import { containsPhoneWords } from 'App/babel/helpers/phoneWords';
import Relation, { RelationContact } from 'App/resources/Model/EntityRelation';
import Ticket from 'App/resources/Model/Ticket';
import Agreement from 'App/resources/Model/Agreement';

export const orderIcon = 'dollar';

export const opportunityRelationMapper = (obj: Order, contact?: RelationContact): Relation | null => {
	if (!obj) {
		return null;
	}

	const masterCurrency =
		Tools.AppService.getMetadata().customerCurrencies.find(c => !!c.masterCurrency)?.iso || 'SEK';
	return {
		type: 'opportunity',
		id: obj.id,
		title: obj.description,
		subtitle: `${currencyFormat(obj.value, masterCurrency)} • ${moment(obj.date).format('YYYY-MM-DD')} • ${
			obj.stage.name
		}`,
		icon: obj.probability === 100 ? orderIcon : 'opportunity',
		withContact: !!contact?.id && obj.contact?.id === contact?.id,
		date: obj.date
	};
};

export const appointmentRelationMapper = (
	obj: Appointment,
	userId: number,
	contact?: RelationContact
): Relation | null => {
	if (!obj) {
		return null;
	}

	return {
		type: 'appointment',
		id: obj.id,
		title: obj.description,
		subtitle: `${moment(obj.date).format('L LT')} • ${getAttendingStr(userId, obj.users, obj.contacts)}`,
		icon: 'calendar',
		withContact: !!(contact?.id && obj.contacts?.find(c => c.id === contact?.id)),
		date: obj.date
	};
};

export const activityRelationMapper = (obj: Activity, userId: number, contact?: RelationContact): Relation | null => {
	if (!obj) {
		return null;
	}

	const todoTypes = Tools.AppService.getTodoTypes();
	const isTodo = obj.activityType?.id === todoTypes.TODO.id;
	const isPhoneCall = obj.activityType?.id === todoTypes.PHONE_CALL.id;
	const activityType = isPhoneCall ? 'phonecall' : isTodo ? 'todo' : 'activity';

	return {
		type: 'activity',
		activityType,
		id: obj.id,
		title: obj.description,
		subtitle: obj.date
			? `${moment(obj.date).format(obj.time ? 'L LT' : 'L')} • ${getAttendingStr(
					userId,
					obj.users,
					obj.contacts
			  )}`
			: `${getAttendingStr(userId, obj.users, obj.contacts)}`,
		icon: containsPhoneWords(obj.activityType?.name) ? 'phone' : 'activity',
		withContact: !!(contact?.id && obj.contacts?.find(c => c.id === contact?.id)),
		date: obj.date
	};
};

export const projectPlanRelationMapper = (obj: ProjectPlan, contact?: RelationContact): Relation | null => {
	if (!obj) {
		return null;
	}

	return {
		type: 'projectPlan',
		id: obj.id,
		title: obj.name,
		subtitle: `${obj.projectPlanType.name} • ${obj.projectPlanStage.name}${
			obj.endDate ? ` • ${moment(obj.endDate).format('YYYY-MM-DD')}` : ''
		}`,
		icon: 'project',
		withContact: !!contact?.id && obj.contact?.id === contact?.id,
		date: null
	};
};
export const ticketRelationMapper = (obj: any, userId: number, contact?: RelationContact): Relation | null => {
	if (!Tools.FeatureHelper.hasSoftDeployAccess('TICKETS_RELATE_TASKS_AND_TICKETS')) {
		return null;
	}
	if (!obj) {
		return null;
	}
	return {
		type: 'ticket',
		id: obj.id,
		title: obj.title,
		subtitle: `${T('default.id2')}: ${obj.id} • ${T('ticket.assignee')} - ${
			obj.contact?.name || T('support.unassigned')
		}`,
		icon: 'customer-support',
		withContact: !!contact?.id && obj.contact?.id === contact?.id,
		date: obj.regDate
	};
};

export const agreementRelationMapper = (obj: Agreement, contact?: RelationContact): Relation | null => {
	if (!Tools.FeatureHelper.hasSoftDeployAccess('SUBSCRIPTION_RELATE_TO_TASK_SUPPORT')) {
		return null;
	}
	if (!obj) {
		return null;
	}

	const masterCurrency =
		Tools.AppService.getMetadata().customerCurrencies.find(c => !!c.masterCurrency)?.iso || 'SEK';

	const agreementIntervalPeriod = T(`order.recurringInterval.byMonth.${obj.metadata.agreementIntervalPeriod}`);

	return {
		type: 'agreement',
		id: obj.id,
		title: `${T('default.agreement')} - ${obj.description}`,
		subtitle: `${currencyFormat(obj.yearlyValue || 0, masterCurrency)} • ${T('default.renewal')} - ${moment(
			obj.metadata.agreementRenewalDate
		).format('YYYY-MM-DD')} • ${agreementIntervalPeriod}`,
		icon: 'subscription',
		withContact: !!contact?.id && obj.contact?.id === contact?.id,
		date: obj.metadata.agreementStartdate
	};
};

const mapRelations = (
	opportunities: Order[],
	appointments: Appointment[],
	activities: Activity[],
	projectPlans: ProjectPlan[],
	tickets: Ticket[],
	agreements: Agreement[],
	userId: number,
	contact?: RelationContact
) =>
	[
		...opportunities.map(rel => opportunityRelationMapper(rel, contact)),
		...appointments.map(rel => appointmentRelationMapper(rel, userId, contact)),
		...activities.map(rel => activityRelationMapper(rel, userId, contact)),
		...projectPlans.map(rel => projectPlanRelationMapper(rel, contact)),
		...tickets.map(rel => ticketRelationMapper(rel, userId, contact)),
		...agreements.map(rel => agreementRelationMapper(rel, contact))
	].filter(Boolean) as Relation[];

export default mapRelations;
